import { computed, Injectable, signal } from '@angular/core';
import { Device as LegacyDevice } from '../class/Device';
import { Device } from '../database/models';
import SocketLocation from '../database/models/SocketLocation';

export type FullDevice = Device & SocketLocation & LegacyDevice;

/**
 * Holds the devices map shared between DevicesManagerService (owns the
 * business logic: aggregation, sockets, filters) and
 * UnitsService/DriversService/LandmarksService/DeviceEditService (read/write
 * it), without either of those depending on DevicesManagerService for it -
 * DevicesManagerService itself depends on UnitsService, so that dependency
 * would otherwise close a circular-DI loop (NG0200).
 *
 * The map is immutable from this service's point of view: every writer must
 * hand over a fresh object reference, since the signal's Object.is equality
 * check silently drops a re-published, mutated-in-place reference.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceStateService {
  private _$devicesMap = signal<Record<number, FullDevice>>(null);

  public $devicesMap = this._$devicesMap.asReadonly();
  public $devices = computed(() => Object.values(this._$devicesMap() || {}));

  public get(uid: number): FullDevice {
    return this._$devicesMap()?.[uid];
  }

  public setDevices(devicesMap: Record<number, FullDevice>): void {
    this._$devicesMap.set(devicesMap);
  }

  public upsertDevice(uid: number, device: FullDevice): void {
    this._$devicesMap.update(devicesMap => ({ ...(devicesMap || {}), [uid]: device }));
  }

  public clear(): void {
    this._$devicesMap.set({});
  }
}
